/**
 * All-entities tax reserve aggregate: jurisdiction first, then entity.
 *
 * WHY (2026-08-30, Fan): with "All Entities" selected the Tax Reserves page
 * showed nothing but "Select an Entity". Tax is owed per jurisdiction, so that
 * is the top level here, with each entity underneath it.
 *
 * The actual/projected split is the point of the layout, not decoration:
 * scheduled recurring costs run to December while revenue is only recognised
 * up to today, so the remaining months of the year look loss-making and their
 * corp-tax estimate goes negative. Only the "to date" columns are a liability.
 */
import { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, Loader2, Info, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { taxReservesGroupAPI } from '../lib/api';
import { TaxReserveCountryMonths, FOCUS_COUNTRIES } from './TaxReserveCountryMonths';
import { CurrencyToggle } from './CurrencyToggle';
import { usePortfolioPrefsStore } from '../store';
import { formatCurrency } from '../lib/chartFormatters';
import { cn } from '../lib/utils';
import { getErrorMessage } from '../lib/errors';

const MONTH_NAMES = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function num(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function StepLine({ label, value, strong }) {
  return (
    <div className={cn('flex justify-between gap-6 max-w-xl', strong && 'font-semibold border-t pt-0.5')}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

// How the corporation tax to date is built, line by line, in the same order
// as services/corp_tax_estimate.py. Every figure comes from the API.
export function CorpTaxEstimateSteps({ est, entity }) {
  const ccy = est.currency || entity.currency;
  const fmt = (v) => formatCurrency(num(v), ccy);
  const months = est.months || entity.actual?.month_count || 12;
  const byAccounts = est.basis === 'filed_accounts_margin';
  return (
    <div className="space-y-0.5 pb-2" data-testid={`corp-tax-steps-${entity.entity_id}`}>
      <p className="text-muted-foreground pb-1">
        {byAccounts
          ? `Corporation tax is estimated from the ${est.accounts_source ? 'filed accounts' : 'accounts'} margin, not from the property P&L below: the P&L has no overheads, interest or depreciation.`
          : 'No filed accounts loaded: the property P&L profit is used as the base (no overheads).'}
      </p>
      {byAccounts && (
        <StepLine
          label={`Revenue to date ${fmt(entity.actual?.revenue)} x ${num(est.margin_pct).toFixed(2)}% margin`}
          value={fmt(est.profit_before_losses)}
        />
      )}
      {!byAccounts && <StepLine label="Property P&L profit to date" value={fmt(est.profit_before_losses)} />}
      {num(est.losses_used) > 0 && <StepLine label="Less losses brought forward" value={`-${fmt(est.losses_used)}`} />}
      {num(est.group_relief) > 0 && <StepLine label="Less UK group relief" value={`-${fmt(est.group_relief)}`} />}
      <StepLine label="Taxable profit to date" value={fmt(est.taxable_profit)} strong />
      {num(est.taxable_profit) > 0 && (
        <>
          <StepLine label={`Annualised (x 12 / ${months})`} value={fmt(est.annualised_profit)} />
          <StepLine label={`Tax for a full year (${est.rate_note})`} value={fmt(est.annual_tax)} />
          <StepLine label={`Corporation tax to date (x ${months} / 12)`} value={fmt(est.tax)} strong />
        </>
      )}
      {num(est.taxable_profit) <= 0 && <StepLine label="Corporation tax to date" value={fmt(0)} strong />}
      {est.full_year_tax != null && (
        <StepLine
          label={`Full year incl. projected months: ${fmt(est.full_year_tax)}, so rest of year`}
          value={fmt(entity.projected?.corp_tax_reserve)}
        />
      )}
      {est.accounts_source && (
        <p className="text-[10px] text-muted-foreground pt-1">Accounts: {est.accounts_source}</p>
      )}
    </div>
  );
}

function EntityRow({ entity }) {
  const [open, setOpen] = useState(false);
  const canonical = entity.by_currency;
  const projectedNegative = entity.projected?.negative_corp_tax_months ?? [];
  // Corporation tax estimate (accounts margin, losses, group relief, real
  // rates). When present it IS the headline, so the dropdown shows its steps,
  // not the old flat-rate figure (Fan, 2026-10-27).
  const ctEst = entity.corp_tax_estimate_detail && entity.corp_tax_estimate_detail.tax != null
    ? entity.corp_tax_estimate_detail
    : null;

  if (canonical) {
    return (
      <>
        <tr
          className="border-b hover:bg-muted/40 cursor-pointer"
          onClick={() => setOpen((prior) => !prior)}
          data-testid={`group-entity-${entity.entity_id}`}
        >
          <td className="py-2 pl-2">
            <span className="inline-flex items-center gap-1 font-medium">
              {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              {entity.name}
            </span>
          </td>
          <td className="py-2 text-xs text-muted-foreground">
            <Badge variant="outline" className="text-[10px]">cash basis</Badge>
          </td>
          <td className="py-2 text-right tabular-nums" colSpan={3}>
            {entity.in_display_currency ? (
              <span className="text-xs">
                <span className="text-muted-foreground mr-1">
                  {entity.in_display_currency.currency}
                </span>
                VAT on invoices{' '}
                <strong>
                  {formatCurrency(
                    num(entity.vat_from_invoices_in_display_currency?.net_vat),
                    entity.in_display_currency.currency,
                  )}
                </strong>
                <span className="text-muted-foreground mx-1">&middot;</span>
                reserved{' '}
                {formatCurrency(
                  num(entity.in_display_currency.vat_reserve),
                  entity.in_display_currency.currency,
                )}
                <span className="text-muted-foreground mx-1">&middot;</span>
                corp tax{' '}
                {formatCurrency(
                  num(entity.in_display_currency.corp_tax_reserve),
                  entity.in_display_currency.currency,
                )}
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">
                multi-currency &mdash; expand
              </span>
            )}
          </td>
        </tr>
        {open && (
          <tr className="border-b bg-muted/20">
            <td colSpan={5} className="px-4 py-3">
              {/* Month by month first: a lifetime-to-date total answers no
                  operating question (Fan, 2026-09-03). */}
              <TaxReserveCountryMonths />
              {entity.by_country?.length > 0 && (
                <div className="mb-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    Reserves to date &mdash; Germany, UK, Ireland, France
                    <span className="normal-case font-normal tracking-normal ml-1">
                      (other countries are in the month table above, under
                      &ldquo;show every country&rdquo;)
                    </span>
                  </p>
                  <table className="w-full text-xs">
                    <thead className="text-muted-foreground">
                      <tr>
                        <th className="text-left font-medium py-1">Country</th>
                        <th className="text-left font-medium py-1">Currency</th>
                        <th className="text-right font-medium py-1">VAT payable (net)</th>
                        <th className="text-right font-medium py-1">Corp tax reserve</th>
                        <th className="text-right font-medium py-1">Operating cash</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entity.by_country
                        .filter((countryRow) => FOCUS_COUNTRIES.includes(countryRow.country))
                        .map((countryRow) => (
                        <tr key={`${countryRow.country}-${countryRow.currency}`}>
                          <td className="py-1">
                            {countryRow.country}
                            {countryRow.country === 'Unstamped' && (
                              <span className="text-[10px] text-amber-600 ml-1">
                                no country on booking
                              </span>
                            )}
                          </td>
                          <td className="py-1 font-mono">{countryRow.currency}</td>
                          <td className="py-1 text-right">
                            {formatCurrency(num(countryRow.vat_reserve), countryRow.currency)}
                            <span className="text-muted-foreground ml-1">
                              (out {formatCurrency(num(countryRow.output_vat), countryRow.currency)}
                              {' '}less in {formatCurrency(num(countryRow.input_vat), countryRow.currency)})
                            </span>
                          </td>
                          <td className="py-1 text-right">
                            {formatCurrency(num(countryRow.corp_tax_reserve), countryRow.currency)}
                          </td>
                          <td className="py-1 text-right">
                            {formatCurrency(num(countryRow.operating_cash), countryRow.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <table className="w-full text-xs">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="text-left font-medium py-1">Currency</th>
                    <th className="text-right font-medium py-1">VAT payable (net)</th>
                    <th className="text-right font-medium py-1">Corp tax reserve</th>
                    <th className="text-right font-medium py-1">Operating cash</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(canonical).map(([currency, buckets]) => (
                    <tr key={currency}>
                      <td className="py-1 font-mono">{currency}</td>
                      <td className="py-1 text-right">{formatCurrency(num(buckets.vat_reserve), currency)}</td>
                      <td className="py-1 text-right">{formatCurrency(num(buckets.corp_tax_reserve), currency)}</td>
                      <td className="py-1 text-right">{formatCurrency(num(buckets.operating_cash), currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {Object.keys(entity.vat_from_invoices ?? {}).length > 0 && (
                <div className="mt-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    VAT from invoices (output less input, per currency)
                  </p>
                  <table className="w-full text-xs">
                    <tbody>
                      {Object.entries(entity.vat_from_invoices).map(([currency, totals]) => (
                        <tr key={currency}>
                          <td className="py-1 font-mono">{currency}</td>
                          <td className="py-1 text-right">
                            output {formatCurrency(num(totals.output_vat), currency)}
                          </td>
                          <td className="py-1 text-right text-muted-foreground">
                            input {formatCurrency(num(totals.input_vat), currency)}
                          </td>
                          <td className="py-1 text-right font-medium">
                            net {formatCurrency(num(totals.net_vat), currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {entity.vat_from_invoices_in_display_currency?.unconverted_currencies?.length > 0 && (
                    <p className="text-[11px] text-amber-600 mt-1">
                      No FX rate for{' '}
                      {entity.vat_from_invoices_in_display_currency.unconverted_currencies.join(', ')}
                      &nbsp;&mdash; excluded from the converted total rather than guessed.
                    </p>
                  )}
                </div>
              )}
              {entity.note && (
                <p className="text-xs text-muted-foreground mt-2">{entity.note}</p>
              )}
            </td>
          </tr>
        )}
      </>
    );
  }

  return (
    <>
      <tr
        className="border-b hover:bg-muted/40"
        data-testid={`group-entity-${entity.entity_id}`}
      >
        <td className="py-2 pl-2">
          <button
            type="button"
            className="inline-flex items-center gap-1 font-medium hover:underline"
            onClick={() => setOpen((prior) => !prior)}
          >
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            {entity.name}
          </button>
        </td>
        <td className="py-2 text-xs text-muted-foreground font-mono">{entity.currency}</td>
        <td className="py-2 text-right tabular-nums">
          {entity.vat_registered ? (
            formatCurrency(num(entity.vat_reserve), entity.currency)
          ) : (
            <span
              className="text-xs text-muted-foreground"
              title={entity.vat_note ?? 'Not VAT-registered'}
              data-testid={`not-vat-registered-${entity.entity_id}`}
            >
              not registered
            </span>
          )}
        </td>
        <td className="py-2 text-right tabular-nums">
          {formatCurrency(num(entity.corp_tax_reserve), entity.currency)}
          {entity.tax_basis === 'personal_income_tax' && (
            <span
              className="block text-[10px] text-muted-foreground"
              data-testid={`personal-tax-basis-${entity.entity_id}`}
            >
              personal income tax {entity.tax_rate_pct ?? '\u2014'}%
            </span>
          )}
        </td>
        <td className="py-2 text-right text-xs text-muted-foreground tabular-nums">
          {entity.projected
            ? formatCurrency(num(entity.projected.corp_tax_reserve), entity.currency)
            : '\u2014'}
          {projectedNegative.length > 0 && (
            <span
              className="ml-1 text-amber-600"
              title={`Projected months with costs but unrecognised revenue: ${projectedNegative.map((m) => MONTH_NAMES[m]).join(', ')}`}
              data-testid={`projected-negative-${entity.entity_id}`}
            >
              ({projectedNegative.map((m) => MONTH_NAMES[m]).join(', ')})
            </span>
          )}
        </td>
      </tr>
      {open && (
        <tr className="border-b bg-muted/20">
          <td colSpan={5} className="px-6 py-3 text-xs space-y-1">
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              <span>VAT rate: <strong>{entity.vat_rate_pct ?? '\u2014'}%</strong></span>
              {ctEst ? (
                <span>
                  {entity.tax_basis_label ?? 'Corp tax'}: <strong>estimate</strong>
                </span>
              ) : (
                <span>
                  {entity.tax_basis_label ?? 'Corp tax'} rate:{' '}
                  <strong>{entity.corp_tax_rate_pct ?? '\u2014'}%</strong>
                </span>
              )}
              <span>Months to date: <strong>{entity.actual?.month_count ?? 0}</strong></span>
              <span>Projected months: <strong>{entity.projected?.month_count ?? 0}</strong></span>
              <span>Basis: <span className="font-mono">{entity.source}</span></span>
            </div>
            {/* Fan, 2026-09-03: "I don't even know what the base is, because
                there are no details!" - show the base the rate is applied to,
                and the months behind it. */}
            {entity.month_detail?.length > 0 && (
              <div data-testid={`tax-base-${entity.entity_id}`} className="pt-1">
                {ctEst ? (
                  <CorpTaxEstimateSteps est={ctEst} entity={entity} />
                ) : (
                  <p className="text-muted-foreground">{entity.tax_base_note}</p>
                )}
                <table className="w-full text-[11px] mt-1">
                  <thead className="text-muted-foreground">
                    <tr>
                      <th className="text-left font-medium py-0.5">Month</th>
                      <th className="text-right font-medium py-0.5">Revenue</th>
                      <th className="text-right font-medium py-0.5">Costs</th>
                      <th className="text-right font-medium py-0.5">{ctEst ? 'Property P&L profit' : 'Net profit (base)'}</th>
                      <th className="text-right font-medium py-0.5">
                        {ctEst
                          ? 'Taxable profit (est.)'
                          : entity.tax_basis === 'personal_income_tax' ? 'Income tax' : 'Corp tax'}
                      </th>
                      <th className="text-right font-medium py-0.5">VAT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entity.month_detail.map((row) => (
                      <tr key={row.month} className={row.is_actual ? '' : 'text-muted-foreground'}>
                        <td className="py-0.5">
                          {MONTH_NAMES[row.month]}
                          {!row.is_actual && <span className="ml-1">(projected)</span>}
                        </td>
                        <td className="text-right py-0.5">{formatCurrency(num(row.revenue), entity.currency)}</td>
                        <td className="text-right py-0.5">{formatCurrency(num(row.costs), entity.currency)}</td>
                        <td className="text-right py-0.5">{formatCurrency(num(row.net_profit), entity.currency)}</td>
                        <td className="text-right py-0.5">
                          {formatCurrency(num(ctEst ? row.estimated_taxable_profit : row.estimated_corp_tax_reserve), entity.currency)}
                        </td>
                        <td className="text-right py-0.5">{formatCurrency(num(row.estimated_vat_reserve), entity.currency)}</td>
                      </tr>
                    ))}
                    <tr className="border-t font-medium">
                      <td className="py-0.5">Months to date</td>
                      <td className="text-right py-0.5">{formatCurrency(num(entity.actual?.revenue), entity.currency)}</td>
                      <td className="text-right py-0.5">{formatCurrency(num(entity.actual?.costs), entity.currency)}</td>
                      <td className="text-right py-0.5">{formatCurrency(num(entity.actual?.net_profit), entity.currency)}</td>
                      <td className="text-right py-0.5">
                        {formatCurrency(num(ctEst ? ctEst.profit_before_losses : entity.actual?.corp_tax_reserve), entity.currency)}
                      </td>
                      <td className="text-right py-0.5">{formatCurrency(num(entity.actual?.vat_reserve), entity.currency)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            {entity.note && (
              <p className="text-muted-foreground flex items-start gap-1 pt-1">
                <Info className="h-3 w-3 mt-0.5 shrink-0" />
                {entity.note}
              </p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}


/**
 * Fan, 2026-09-03: "make it stat cards and summarise the corp tax by entity
 * rather than by currency. As the corp tax is paid in the country of the
 * residence." The old header stacked one line per currency (DKK/EUR/GBP/USD),
 * which mixes entities that owe tax in different countries. Corp tax follows
 * the entity's country of residence, so the entity is the right unit here.
 */
function entityDisplayTotals(entity) {
  const display = entity.in_display_currency;
  if (display) {
    return {
      currency: display.currency,
      vat: num(entity.vat_from_invoices_in_display_currency?.net_vat ?? display.vat_reserve),
      corpTax: num(display.corp_tax_reserve),
      converted: true,
    };
  }
  return {
    currency: entity.currency,
    vat: entity.vat_registered ? num(entity.vat_reserve) : null,
    corpTax: num(entity.corp_tax_reserve),
    converted: false,
  };
}

function StatCard({ label, sublabel, value, secondary, testId, emphasis }) {
  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        emphasis ? 'bg-muted/50' : 'bg-background',
      )}
      data-testid={testId}
    >
      <p className="text-xs text-muted-foreground truncate" title={label}>{label}</p>
      <p className="text-lg font-bold tabular-nums mt-1">{value}</p>
      {secondary && (
        <p className="text-[11px] text-muted-foreground tabular-nums mt-0.5">{secondary}</p>
      )}
      {sublabel && (
        <p className="text-[10px] text-muted-foreground mt-0.5">{sublabel}</p>
      )}
    </div>
  );
}

export function JurisdictionStatCards({ juris }) {
  const totals = juris.totals_in_display_currency;
  const entities = juris.entities ?? [];
  return (
    <div
      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
      data-testid={`jurisdiction-stat-cards-${juris.jurisdiction || 'unassigned'}`}
    >
      {totals && (
        <StatCard
          emphasis
          label={`${juris.label} \u2014 corp tax to date`}
          value={formatCurrency(num(totals.corp_tax_reserve), totals.currency)}
          secondary={`VAT on invoices ${formatCurrency(num(totals.vat_from_invoices), totals.currency)}`}
          sublabel={`${entities.length} ${entities.length === 1 ? 'entity' : 'entities'}`}
          testId={`jurisdiction-total-card-${juris.jurisdiction || 'unassigned'}`}
        />
      )}
      {entities.map((entity) => {
        const stats = entityDisplayTotals(entity);
        return (
          <StatCard
            key={entity.entity_id}
            label={entity.name}
            value={formatCurrency(stats.corpTax, stats.currency)}
            secondary={
              stats.vat === null
                ? 'not VAT-registered'
                : `VAT ${formatCurrency(stats.vat, stats.currency)}`
            }
            sublabel={
              entity.tax_basis === 'personal_income_tax'
                ? `personal income tax ${entity.tax_rate_pct ?? '\u2014'}%`
                : entity.corp_tax_estimate_detail?.tax != null && entity.corp_tax_estimate_detail?.basis
                  ? `${entity.tax_basis_label ?? 'Corp tax'} estimate`
                  : `${entity.tax_basis_label ?? 'Corp tax'} ${entity.corp_tax_rate_pct ?? '\u2014'}%`
            }
            testId={`entity-corp-tax-card-${entity.entity_id}`}
          />
        );
      })}
    </div>
  );
}

export function TaxReservesGroupSummary({ year }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const displayCurrency = usePortfolioPrefsStore((s) => s.displayCurrency);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await taxReservesGroupAPI.getGroupSummary(year, displayCurrency);
      setData(response.data);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [year, displayCurrency]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-destructive" data-testid="group-summary-error">
          {error}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="tax-reserves-group-summary">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground flex items-start gap-1">
          <Info className="h-3 w-3 mt-0.5 shrink-0" />
          Reserves owed to date, per jurisdiction. VAT is aggregated from the
          invoices and shown only for VAT-registered entities. Corporation tax
          is an estimate from each company&apos;s latest accounts margin; open a
          company to see how it is built. The projected column is the rest of {data?.year}.
        </p>
        <CurrencyToggle className="shrink-0" />
      </div>
      {Object.keys(data?.fx_rates_used ?? {}).length > 0 && (
        <p className="text-[11px] text-muted-foreground" data-testid="group-summary-fx-rates">
          Converted at{' '}
          {Object.entries(data.fx_rates_used)
            .filter(([currency]) => currency !== data.display_currency)
            .map(([currency, rate]) => `1 ${currency} = ${Number(rate).toFixed(5)} ${data.display_currency}`)
            .join(' \u00b7 ')}
        </p>
      )}

      {(data?.jurisdictions ?? []).map((juris) => (
        <Card key={juris.jurisdiction || 'unassigned'} data-testid={`jurisdiction-${juris.jurisdiction || 'unassigned'}`}>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="text-base">{juris.label}</CardTitle>
                <CardDescription className="text-xs">
                  {juris.entities.length} {juris.entities.length === 1 ? 'entity' : 'entities'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <JurisdictionStatCards juris={juris} />
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b">
                <tr>
                  <th className="text-left font-medium py-1 pl-2">Entity</th>
                  <th className="text-left font-medium py-1">Currency</th>
                  <th className="text-right font-medium py-1">VAT to date</th>
                  <th className="text-right font-medium py-1">Corp tax to date</th>
                  <th className="text-right font-medium py-1">Rest of year (projected)</th>
                </tr>
              </thead>
              <tbody>
                {juris.entities.map((entity) => (
                  <EntityRow key={entity.entity_id} entity={entity} />
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}

      {(data?.warnings ?? []).length > 0 && (
        <Card>
          <CardContent className="py-3 text-xs space-y-1" data-testid="group-summary-warnings">
            {data.warnings.map((warning) => (
              <p key={warning} className="flex items-start gap-1 text-amber-600">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                {warning}
              </p>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default TaxReservesGroupSummary;
